import { useCallback, useEffect, useMemo, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import LocationIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  DoctorInterviewView,
  InterviewParticipantStatus,
} from "@thalamos/aspire-common";
import {
  notificationChannelFns,
  NotificationEvents,
} from "@thalamos/aspire-common";

import {
  InterviewDetailsDialog,
  type InterviewDetailsDialogApi,
} from "./InterviewDetailsDialog.js";
import { api } from "~/api.js";
import { FormTitle } from "~/components/design-system/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";
import { useChannel } from "~/lib/pusher/useChannel.js";
import { useEvent } from "~/lib/pusher/useEvent.js";

// ---------------------------------------------------------------------------
// API interface for dependency injection (testing / stories)
// ---------------------------------------------------------------------------

export interface DoctorInterviewsSectionApi {
  getMyInterviews: (
    formContextId: string,
  ) => Promise<{ data: DoctorInterviewView[] }>;
}

const defaultApi: DoctorInterviewsSectionApi = {
  getMyInterviews: (formContextId) =>
    api.interviews.getMyInterviews(formContextId),
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function getStatusLabel(status: InterviewParticipantStatus): string {
  switch (status) {
    case "accepted":
      return "Accepted";
    case "confirmed":
      return "Confirmed";
    case "pending-confirmed":
      return "Awaiting your response";
    default:
      return status;
  }
}

function getStatusColor(
  status: InterviewParticipantStatus,
): "default" | "success" | "primary" | "info" {
  switch (status) {
    case "accepted":
      return "success";
    case "confirmed":
      return "primary";
    case "pending-confirmed":
      return "info";
    default:
      return "default";
  }
}

function getStatusIcon(status: InterviewParticipantStatus) {
  switch (status) {
    case "confirmed":
      return <CheckCircleIcon fontSize="small" />;
    case "pending-confirmed":
    case "accepted":
    default:
      return <PendingIcon fontSize="small" />;
  }
}

function getStatusTextColor(status: InterviewParticipantStatus): string {
  switch (status) {
    case "pending":
      return "#0B122C";
    case "accepted":
    case "rejected":
    case "confirmed":
    case "pending-confirmed":
      return "#FFFFFF";
    case "revoked":
      return "#0B122C";
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DoctorInterviewsSectionProps {
  formContextId: string;
  /** Optional API override for testing / Storybook */
  apiOverride?: DoctorInterviewsSectionApi;
  /** Optional dialog API override for testing / Storybook */
  dialogApiOverride?: InterviewDetailsDialogApi;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DoctorInterviewsSection({
  formContextId,
  apiOverride,
  dialogApiOverride,
}: DoctorInterviewsSectionProps) {
  const apiClient = apiOverride ?? defaultApi;
  const [expanded, setExpanded] = useState(true);
  const [interviews, setInterviews] = useState<DoctorInterviewView[] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState<{
    interview: DoctorInterviewView;
    label: string;
  } | null>(null);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getMyInterviews(formContextId);
      setInterviews(Array.isArray(response.data) ? response.data : []);
    } catch {
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [formContextId, apiClient]);

  useEffect(() => {
    void loadInterviews();
  }, [loadInterviews]);

  // Subscribe to pushed notifications so interviews auto-reload when changed
  const interviewsChannel = useChannel(
    notificationChannelFns.interviews(formContextId),
  );
  useEvent(interviewsChannel, NotificationEvents.resourceReload, () => {
    void loadInterviews();
  });

  const activeInterviews = useMemo(
    () =>
      (interviews ?? [])
        .filter((i) => i.status !== "cancelled")
        .sort((a, b) => a.created.localeCompare(b.created)),
    [interviews],
  );

  // Nothing to show while initial load is running
  if (loading && interviews === null) {
    return (
      <Container addMarginBottom>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <FormTitle
            subtitleText="My MHA Assessments"
            hasContainerMarginBottom={false}
            subTitleTextFontSize={2}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Container>
    );
  }

  // Nothing to show if no visible interviews
  if (activeInterviews.length === 0) {
    return null;
  }

  return (
    <Container addMarginBottom>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
        }}
      >
        <FormTitle
          subtitleText={`My MHA Assessments (${activeInterviews.length})`}
          hasContainerMarginBottom={false}
          subTitleTextFontSize={2}
        />
        <Box sx={{ ml: "auto" }}>
          <ExpandMore
            role="button"
            isExpanded={expanded}
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label="Toggle my MHA assessments section"
          >
            <ExpandMoreIcon sx={{ color: "primary.main" }} />
          </ExpandMore>
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {activeInterviews.map((interview, index) => {
            const label = `MHA Assessment ${index + 1}`;
            return (
              <CompactInterviewRow
                key={interview.id}
                interview={interview}
                label={label}
                onViewDetails={() => setSelectedInterview({ interview, label })}
              />
            );
          })}
        </Box>
      )}

      {/* Details dialog */}
      {selectedInterview && (
        <InterviewDetailsDialog
          open
          onClose={() => setSelectedInterview(null)}
          interview={selectedInterview.interview}
          label={selectedInterview.label}
          apiOverride={dialogApiOverride}
        />
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Compact interview row — single row per interview
// ---------------------------------------------------------------------------

function CompactInterviewRow({
  interview,
  label,
  onViewDetails,
}: {
  interview: DoctorInterviewView;
  label: string;
  onViewDetails: () => void;
}) {
  const hasAddress = interview.address && !interview.locationUnknown;

  const briefLocation = hasAddress
    ? `${interview.address}${interview.postalCode ? `, ${interview.postalCode}` : ""}`
    : interview.locationUnknown
      ? "Location TBC"
      : null;

  const firstTimeRange = interview.timeRanges[0];
  const briefTime = firstTimeRange
    ? dayjs(firstTimeRange.start).format("ddd D MMM YYYY, HH:mm")
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        flexWrap: "wrap",
      }}
      data-testid="compact-interview-row"
    >
      {/* Label + status chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: "1 1 auto",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {label}
        </Typography>
        <Chip
          icon={getStatusIcon(interview.myParticipant.status)}
          label={getStatusLabel(interview.myParticipant.status)}
          size="small"
          color={getStatusColor(interview.myParticipant.status)}
          variant="filled"
          sx={{
            "& .MuiChip-label": {
              color: getStatusTextColor(interview.myParticipant.status),
            },
            "& .MuiChip-icon": {
              color: `${getStatusTextColor(interview.myParticipant.status)} !important`,
            },
          }}
        />
      </Box>

      {/* Brief location + time */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          color: "text.secondary",
          minWidth: 0,
          flex: "1 1 auto",
        }}
      >
        {briefLocation && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <LocationIcon
              sx={{ fontSize: "1rem", flexShrink: 0 }}
              color="action"
            />
            <Typography
              variant="body2"
              noWrap
              data-testid="assessment-location"
            >
              {briefLocation}
            </Typography>
          </Box>
        )}
        {briefTime && (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "nowrap" }}
            data-testid="assessment-time"
          >
            {briefTime}
          </Typography>
        )}
      </Box>

      {/* View Details button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<VisibilityIcon fontSize="small" />}
        onClick={onViewDetails}
        data-testid="view-details-button"
        sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
      >
        View Details
      </Button>
    </Box>
  );
}
